import { useCallback, useEffect, useState } from "react";
import { MailPlus, UserRoundX, UserRoundCheck } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { api, formatApiError } from "@/lib/api";

function formatStamp(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function ClientUsersTable({ users, onToggle, onResend, busyId, loaded = true }) {
  return (
    <section className="board-table-card client-users-panel" data-testid="client-users-panel">
      <div className="section-heading">
        <div>
          <h2>Client users</h2>
          <p data-testid="client-users-count">{users.filter((item) => item.active).length} active · {users.filter((item) => item.kind === "invite").length} invited · {users.filter((item) => item.kind !== "invite" && !item.active).length} deactivated</p>
        </div>
      </div>
      <div className="board-table-scroll">
        <table className="board-table" data-testid="client-users-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Client</th><th>Status</th><th>Last sign-in</th><th aria-label="Actions" /></tr>
          </thead>
          <tbody>
            {users.map((item) => (
              <tr className={`board-row board-row-static ${item.active ? "" : "client-user-inactive"}`} key={item.id} data-testid={`client-user-${item.id}`}>
                <td data-label="Name"><strong>{item.first_name} {item.last_name}</strong></td>
                <td data-label="Email">{item.email}</td>
                <td data-label="Client">{item.client_name || <span className="cell-dash">No client linked</span>}</td>
                <td data-label="Status">
                  {item.kind === "invite" ? (
                    <span className={`status-badge ${item.invite_expired ? "status-inactive" : "status-pending"}`} data-testid={`client-user-status-${item.id}`}>{item.invite_expired ? "Invitation expired" : "Invited · not set up"}</span>
                  ) : (
                    <span className={`status-badge ${item.active ? "status-active" : "status-inactive"}`} data-testid={`client-user-status-${item.id}`}>{item.active ? (item.password_set ? "Active" : "Active · no password") : "Deactivated"}</span>
                  )}
                  {!item.active && item.deactivation_reason && <small className="client-user-reason"> {item.deactivation_reason}</small>}
                </td>
                <td data-label="Last sign-in">{formatStamp(item.last_login_at)}</td>
                <td className="client-user-actions">
                  {(item.kind === "invite" || item.active) && (
                    <button className="table-edit-button" data-testid={`resend-invite-${item.id}`} disabled={busyId === item.id} type="button" onClick={() => onResend(item)}>
                      <MailPlus size={14} /> Resend invitation
                    </button>
                  )}
                  {item.kind !== "invite" && (
                    <button className="table-edit-button" data-testid={`toggle-client-user-${item.id}`} disabled={busyId === item.id} type="button" onClick={() => onToggle(item)}>
                      {item.active ? <><UserRoundX size={14} /> Deactivate</> : <><UserRoundCheck size={14} /> Reactivate</>}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loaded && !users.length && <div className="board-empty" data-testid="client-users-empty">No client users yet. Create a client account and send its invitation below.</div>}
        {!loaded && !users.length && <div className="board-empty list-loading" data-testid="client-users-loading">Loading client users…</div>}
      </div>
    </section>
  );
}

export default function ClientAccountsPage() {
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [client, setClient] = useState({ name: "", region: "", budget_amount: 0 });
  const [invite, setInvite] = useState({ client_id: "", email: "", first_name: "", last_name: "", send_email: false });

  const load = useCallback(async () => {
    try {
      const [clientResponse, userResponse] = await Promise.all([api.get("/clients/budgets"), api.get("/client-accounts/users")]);
      setClients(clientResponse.data.filter((item) => item.active !== false));
      setUsers(userResponse.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function createClient(event) {
    event.preventDefault();
    try {
      await api.post("/client-accounts", { ...client, budget_amount: Number(client.budget_amount) });
      setClient({ name: "", region: "", budget_amount: 0 });
      setMessage("Client account created.");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function createInvite(event) {
    event.preventDefault();
    try {
      const response = await api.post("/client-accounts/invites", invite);
      setMessage(`${response.data.email_sent ? "Invitation emailed. " : "No email sent — share this link yourself: "}${response.data.accept_url || `${window.location.origin}/client-invite/accept?token=${response.data.token}`}`);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function toggleUser(item) {
    const reason = item.active ? window.prompt(`Deactivate ${item.email}? Enter an optional reason. The login is disabled but kept and can be reactivated.`, "") : null;
    if (item.active && reason === null) return;
    setBusyId(item.id);
    setError("");
    try {
      await api.post(`/client-accounts/users/${item.id}/${item.active ? "deactivate" : "reactivate"}`, item.active ? { reason } : {});
      setMessage(`${item.email} ${item.active ? "deactivated" : "reactivated"}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusyId("");
    }
  }

  async function resendInvite(item) {
    if (!window.confirm(`Email a fresh invitation link to ${item.email}? Any earlier invitation link stops working.`)) return;
    setBusyId(item.id);
    setError("");
    try {
      const response = await api.post(`/client-accounts/users/${item.id}/resend-invite`, {});
      setMessage(response.data.email_sent ? `Invitation re-sent to ${response.data.email}.` : `Invitation created but the email could not be sent — share this link yourself: ${response.data.accept_url}`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusyId("");
    }
  }

  return (
    <div className="app-shell" data-testid="client-accounts-shell">
      <AppSidebar activeItem="Client logins" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page" data-testid="client-accounts-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">TAG ADMINISTRATION</p>
              <h1>Client logins</h1>
              <p className="page-subtitle">Create a client account, issue its single secure invitation, and manage existing client logins.</p>
            </div>
          </div>
          {error && <p className="form-message error-message" data-testid="client-account-error">{error}</p>}
          {message && <p className="form-message success-message" data-testid="client-account-message">{message}</p>}
          <ClientUsersTable users={users} busyId={busyId} onToggle={toggleUser} onResend={resendInvite} loaded={loaded} />
          <section className="dashboard-lower-grid">
            <form className="request-form-panel request-form-grid" onSubmit={createClient} data-testid="create-client-form">
              <h2 className="form-full">Create client account</h2>
              <label>Client name<input data-testid="new-client-name" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} required /></label>
              <label>Region<input data-testid="new-client-region" value={client.region} onChange={(e) => setClient({ ...client, region: e.target.value })} required /></label>
              <label className="form-full">Approved budget<input data-testid="new-client-budget" type="number" min="0" value={client.budget_amount} onChange={(e) => setClient({ ...client, budget_amount: e.target.value })} required /></label>
              <button className="button button-primary" data-testid="create-client-button">Create client</button>
            </form>
            <form className="request-form-panel request-form-grid" onSubmit={createInvite} data-testid="create-client-invite-form">
              <h2 className="form-full">Invite authorized client user</h2>
              <label className="form-full">Client<select data-testid="invite-client-select" value={invite.client_id} onChange={(e) => setInvite({ ...invite, client_id: e.target.value })} required><option value="">Select client</option>{clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>First name<input data-testid="invite-first-name" value={invite.first_name} onChange={(e) => setInvite({ ...invite, first_name: e.target.value })} required /></label>
              <label>Last name<input data-testid="invite-last-name" value={invite.last_name} onChange={(e) => setInvite({ ...invite, last_name: e.target.value })} required /></label>
              <label className="form-full">Email<input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} required /></label>
              <label className="form-full checkbox-inline"><input data-testid="invite-send-email" type="checkbox" checked={invite.send_email} onChange={(e) => setInvite({ ...invite, send_email: e.target.checked })} /> Email the invitation link to this person (off by default — you can share the link yourself)</label>
              <button className="button button-primary" data-testid="create-client-invite-button">Create invitation</button>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}
